import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';

const PushNotificationContext = createContext(null);

export function PushNotificationProvider({ children }) {
  const [permission, setPermission] = useState('default');
  const [enabled, setEnabled] = useState(false);
  const [preferences, setPreferences] = useState({
    trade_alerts: true,
    bot_activity: true,
    price_alerts: true,
    big_movers: true,
    system_updates: true,
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
      setEnabled(Notification.permission === 'granted' && localStorage.getItem('push_enabled') === 'true');
    }
    // Load saved preferences
    const saved = localStorage.getItem('push_preferences');
    if (saved) {
      try { setPreferences(JSON.parse(saved)); } catch {}
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return false;
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      setEnabled(true);
      localStorage.setItem('push_enabled', 'true');
      // Register with backend
      if (token) {
        try {
          await axios.post(`${API}/notifications/push-subscribe`, { enabled: true, preferences }, { headers });
        } catch {}
      }
      return true;
    }
    return false;
  }, [token, preferences]);

  const disableNotifications = useCallback(() => {
    setEnabled(false);
    localStorage.setItem('push_enabled', 'false');
    if (token) {
      try {
        axios.post(`${API}/notifications/push-subscribe`, { enabled: false }, { headers });
      } catch {}
    }
  }, [token]);

  const updatePreferences = useCallback((newPrefs) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    localStorage.setItem('push_preferences', JSON.stringify(updated));
    if (token) {
      try {
        axios.post(`${API}/notifications/push-preferences`, updated, { headers });
      } catch {}
    }
  }, [preferences, token]);

  const sendNotification = useCallback((title, options = {}) => {
    if (!enabled || permission !== 'granted') return;
    const category = options.category || 'system_updates';
    if (!preferences[category]) return;

    try {
      new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: options.tag || `bittrade-${Date.now()}`,
        ...options,
      });
    } catch {}
  }, [enabled, permission, preferences]);

  return (
    <PushNotificationContext.Provider value={{
      permission, enabled, preferences,
      requestPermission, disableNotifications,
      updatePreferences, sendNotification,
      isSupported: 'Notification' in window,
    }}>
      {children}
    </PushNotificationContext.Provider>
  );
}

export function usePushNotifications() {
  const ctx = useContext(PushNotificationContext);
  if (!ctx) return {
    permission: 'default', enabled: false, preferences: {},
    requestPermission: async () => false, disableNotifications: () => {},
    updatePreferences: () => {}, sendNotification: () => {},
    isSupported: false,
  };
  return ctx;
}
